import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    NgZone,
    OnDestroy,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core"
import { Subscription } from "rxjs"
import { MuxDataModel } from "../../models/muxData.model"
import { VideoMedia, VideoMediaFile } from "../../models/resource/media"
import { GTMService } from "../../services/gtm.service"
import { ResourceService } from "../../services/resource.service"
import { ScriptService } from "../../services/script-service.service"
import { UserAgentService } from "../../services/user-agent.service"
import { UserService } from "../../services/user.service"
import PlayerUtils from "../../utils/player-utils"
import { GtmVideoJsInfo } from "../../utils/types"

declare const videojs: any

/*
Based on https://docs.videojs.com/tutorial-angular.html.
Duplicated in the most part for audio.component.ts. Please consider this when making changes.
 */
@Component({
    selector: "app-video",
    templateUrl: "./video.component.html",
    styleUrls: ["./video.component.less"],
    // DOM elements added by video.js do not have Angular namespaces and break, so disable this
    encapsulation: ViewEncapsulation.None,
})
export class VideoComponent implements AfterViewInit, OnDestroy {
    @Input() public containerId: string = "videoContainer-" + Date.now()
    @Input() public muxData: MuxDataModel = new MuxDataModel()
    @Input() public gtmVideoJsInfo: GtmVideoJsInfo

    public player: any = null
    public subscriptions: Subscription[] = []
    public crawlerSafeVideoSrc: string
    @ViewChild("player") public target: ElementRef
    public video: VideoMedia
    private viewInit = false
    private firstInitialize: boolean = false

    constructor(
        public userAgentService: UserAgentService,
        private resourceService: ResourceService,
        private zone: NgZone,
        private userService: UserService,
        private scriptService: ScriptService,
        private gtmService: GTMService
    ) {
        this.viewInit = false
    }

    public ngAfterViewInit() {
        this.viewInit = true
        this.updatePlayer()
    }

    public ngOnDestroy() {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe())
        if (this.player) {
            // https://docs.videojs.com/tutorial-player-workflows.html#dispose
            // When the component is destroyed we still want to clean the DOM and the memory
            this.player.dispose()
        }
    }

    @Input()
    public set videoMedia(value: VideoMedia) {
        // Note: maybe the onChanges handling in audio is more efficient if this does multiple updatePlayer calls
        this.video = value
        this.crawlerSafeVideoSrc = value.backup ? value.backup.getURL() : value.usedFile.getURL()
        if (!this.video.isGeoRestrictionEvaluated()) {
            this.subscriptions.push(
                this.video.geoRestrictionEvaluated.subscribe((evaluatedMedia) => {
                    if (evaluatedMedia !== this.video) {
                        return
                    }
                    this.updatePlayer()
                })
            )
        } else {
            this.updatePlayer()
        }
    }

    private updatePlayer() {
        if (
            !this.video ||
            !this.viewInit ||
            !this.video.isGeoRestrictionEvaluated() ||
            this.userAgentService.isGoogleCrawler
        ) {
            return
        }

        this.scriptService.load("videojs", "videojs.css", "videojs-seek-chapter.css").then(() => {
            const videoPlugins = [
                "videojs-mux",
                "videojs-http-source-selector",
                "videojs-seek-buttons",
                "videojs-errors",
                "videojs-hotkeys",
                "videojs-contrib-quality-levels",
                "videojs-seek-chapter",
            ]
            // Plugins must be loaded after video.js is loaded in order to be registered.
            this.scriptService.load(...videoPlugins).then(() => {
                this.initVideoJS()
            })
        })
    }

    private initVideoJS() {
        /*
        We need to account that the component will be kept and the VideoMedia will change (gallery example).
        So instantiate the player only once and assume it needs cleaning.
        https://docs.videojs.com/player#reset preferred, but this would also work:
        this.player.remoteTextTracks().tracks_.forEach((track) => {
            this.player.removeRemoteTextTrack(track);
        });
         */
        if (!this.player) {
            const hotKeysSettings = {
                seekStep: 10,
                enableVolumeScroll: false,
            }

            // First instantiation. Not setting src/cc/poster options here because it would prevent it from being reset
            this.player = videojs(this.target.nativeElement, {
                playbackRates: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
                html5: {
                    nativeTextTracks: false,
                },
                controlBar: {
                    volumePanel: { inline: false },
                },
                plugins: {
                    seekChapter: {
                        chapterDisplayLength: 3,
                    },
                },
            })
            const tech = this.player.tech()
            tech.on("usage", (event) => {
                if (event.name === "hls-gap-skip") {
                    this.logGapSkip()
                }
            })
            if (this.player.seekButtons) {
                this.player.seekButtons({ back: 10 })
            }
            if (this.player.httpSourceSelector) {
                this.player.httpSourceSelector()
            }
            if (this.player.errors) {
                this.player.errors({
                    dismiss: false,
                    errors: PlayerUtils.ErrorOverrides,
                })
            }
            this.player.controlBar.removeChild("pictureInPictureToggle")
            this.player.on("touchstart", (e) => {
                if (e.target.nodeName === "VIDEO") {
                    if (this.player.paused()) {
                        this.player.play()
                    } else {
                        this.player.pause()
                    }
                }
            })
            PlayerUtils.finalizePlayerSetup(
                this.player,
                this.gtmService,
                this.gtmVideoJsInfo,
                hotKeysSettings
            )
            this.firstInitialize = true
        } else {
            // Moved to a different video media in the gallery
            this.firstInitialize = false
            this.player.pause() // Without this, the reset is causing a loading state
            this.player.reset()
        }
        this.updateVideoJS()
    }

    private updateVideoJS() {
        if (this.video.isAvailableToUser()) {
            this.player.poster(this.video.getDefaultPosterImage())
            this.player.src(this.video.getVideoJsSources())

            if (this.video.is360) {
                this.scriptService.load("videojs-vr").then(() => {
                    this.player.vr({ projection: "360" })
                })
            }

            PlayerUtils.loadCaptionLanguage(this.player, this.video)
            PlayerUtils.trackCaptionSettingsState(this.player)
        } else {
            this.player.error({ code: "PLAYER_ERR_GEO_RESTRICTED" })
        }

        this.muxData.setupForVideoJS(this.player, this.firstInitialize)
        this.gtmService.setMediaType(this.gtmVideoJsInfo.type)
        this.gtmService.setVideoInfo(`${this.gtmVideoJsInfo.title} | ${this.gtmVideoJsInfo.guid}`)
    }

    private logGapSkip() {
        if (this.player.qualityLevels) {
            const qualityLevels = this.player.qualityLevels()
            const quality = qualityLevels[qualityLevels.selectedIndex]
            const data = `${this.muxData.title} ${quality.label} ${quality.width} ${quality.height} ${quality.bitrate}`
            this.gtmService.setVideoData(data)
        }
        this.gtmService.pushEvent("hls-gap-skip")
    }
}
